import { useEffect, useId, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { Button } from "./Button";
import { focusablesOf } from "./Popover";

export type ModalProps = {
  open: boolean;
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  closeLabel: string;
  headerTools?: ReactNode;
  footer?: ReactNode;
  width?: string;
  className?: string;
  dataFxmap?: string;
  children: ReactNode;
};

export function Modal({ open, title, subtitle, onClose, closeLabel, headerTools, footer, width, className, dataFxmap, children }: ModalProps) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const element = ref.current;

    if (element !== null) {
      const items = focusablesOf(element).filter(a => a.dataset.fxmapAction !== "close");

      (items[0] ?? element).focus();
    }

    return () => {
      if (previous !== null && document.contains(previous)) previous.focus();
    };
  }, [open]);

  if (!open) return null;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
      return;
    }

    if (event.key !== "Tab" || ref.current === null) return;

    const items = focusablesOf(ref.current);

    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fxmap-dialog-backdrop fxmap-modal-backdrop" onPointerDown={event => event.target === event.currentTarget && onClose()}>
      <div ref={ref} className={["fxmap-modal", "fxmap-card", className ?? ""].filter(Boolean).join(" ")} role="dialog" aria-modal="true" aria-labelledby={titleId} data-fxmap={dataFxmap ?? "modal"} tabIndex={-1} style={width !== undefined ? { width } : undefined} onKeyDown={onKeyDown}>
        <header className="fxmap-modal-header">
          <div className="fxmap-modal-titles">
            <h2 id={titleId} className="fxmap-modal-title fxmap-display fxmap-truncate">
              {title}
            </h2>
            {subtitle ? <span className="fxmap-modal-subtitle fxmap-muted fxmap-truncate">{subtitle}</span> : null}
          </div>
          <div className="fxmap-modal-tools">
            {headerTools}
            <Button kind="icon" size="sm" icon="close" label={closeLabel} onClick={onClose} data-fxmap-action="close" />
          </div>
        </header>
        <div className="fxmap-modal-body">{children}</div>
        {footer !== undefined ? <footer className="fxmap-modal-footer">{footer}</footer> : null}
      </div>
    </div>
  );
}
